import { useEffect, useState } from "react";
import { api, type AttachmentRow } from "../api/client";
import { b64decode, unwrapItemKey } from "../crypto/vault";
import { decryptFilename, downloadAttachment, uploadAttachment } from "../vault/attachments";
import { Paperclip } from "lucide-react";

const MAX_MB = 25;

// Attachments for the selected item (§14). Encryption/decryption happen in the browser;
// downloads require RevealSecret (enforced server-side).
export function AttachmentsPanel({ itemId, orgKey, canEdit }: { itemId: string; orgKey: Uint8Array; canEdit: boolean }) {
  const [itemKey, setItemKey] = useState<Uint8Array | null>(null);
  const [rows, setRows] = useState<AttachmentRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(key?: Uint8Array) {
    const k = key ?? itemKey;
    if (!k) return;
    try {
      const { attachments } = await api.listAttachments(itemId);
      setRows(attachments);
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const summary = await api.getItem(itemId);
        const k = unwrapItemKey(orgKey, b64decode(summary.wrapped_item_key));
        if (!alive) return;
        setItemKey(k);
        const { attachments } = await api.listAttachments(itemId);
        if (alive) setRows(attachments);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [itemId, orgKey]);

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !itemKey) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Max ${MAX_MB} MB`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await uploadAttachment(itemId, itemKey, file);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDownload(row: AttachmentRow) {
    if (!itemKey) return;
    setBusy(true);
    try {
      const { name, data } = await downloadAttachment(row, itemKey);
      const url = URL.createObjectURL(new Blob([data as BlobPart]));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(row: AttachmentRow) {
    if (!window.confirm("Delete this attachment?")) return;
    await api.deleteAttachment(row.id);
    await load();
  }

  return (
    <div className="mt-3 border-t border-border pt-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold tracking-wide text-muted">ATTACHMENTS</span>
        {canEdit && (
          <label className="cursor-pointer text-xs text-accent hover:underline">
            + Upload
            <input type="file" className="hidden" onChange={onUpload} disabled={busy} />
          </label>
        )}
      </div>
      {rows.length === 0 && <div className="text-xs text-muted">No attachments.</div>}
      {rows.map((row) => (
        <div key={row.id} className="flex items-center justify-between gap-2 py-0.5 text-xs">
          <span className="flex items-center gap-1.5 truncate"><Paperclip className="h-3.5 w-3.5 shrink-0" /> {itemKey ? decryptFilename(itemKey, row.enc_filename) : "…"}</span>
          <span className="flex shrink-0 items-center gap-2">
            <span className="text-muted">{(row.size_bytes / 1024).toFixed(0)} KB</span>
            <button onClick={() => onDownload(row)} className="text-accent hover:underline">get</button>
            {canEdit && <button onClick={() => onDelete(row)} className="text-danger hover:underline">del</button>}
          </span>
        </div>
      ))}
      {busy && <div className="text-[11px] text-muted">working…</div>}
      {error && <div className="text-[11px] text-danger">{error}</div>}
    </div>
  );
}
